import api from './api'
import { toCamelCase } from '@/utils/transform'

/**
 * A datapoint the documents state and this platform has no field for.
 *
 * The workbook survey has always reported the labels it could not name. Those
 * reports now accumulate here, ranked by how many DEALS stated the label —
 * the same word ten times in one spreadsheet is one deal's opinion, not ten.
 *
 * The count is the server's reading of the records that still exist, made when
 * the row is read. The tally the harvest kept counted every re-parse's
 * throwaway record as a deal of its own — one workbook cell read as 37 "deals"
 * though none of the records its readings named still existed — so it travels
 * beside the live count, never in its place.
 */
export interface DiscoveredSighting {
  value: number | string | null
  sheet: string | null
  cell: string | null
  document: string | null
  ingestionId: string | null
  seenAt: string | null
  /** The deal the reading belongs to: the deal its record was promoted into,
   *  else its deal group or thread, else the record itself. Absent on readings
   *  an older server stored. */
  dealKey?: string | null
  loanId?: string | null
}

/** One live deal that states the label, and the record it can be opened by. */
export interface DiscoveredDeal {
  dealKey: string
  loanId: string | null
  ingestionId: string
  name: string | null
  receivedAt: string | null
  /** An intake record this account may not open: the server sends its id and
   *  nothing else — no name, no date — and a link to it would be refused.
   *  Absent from an older server. */
  redacted?: boolean
}

/** One source file the live readings came from. */
export interface DiscoveredFile {
  document: string
  dealCount: number
  sightingCount: number
  /** The stored original: its content hash and a record that holds it — the
   *  pair the source viewer opens a file by. Absent from an older server, which
   *  names a file it gives no way to open. */
  sha256?: string | null
  ingestionId?: string | null
}

export type DiscoveredKind = 'currency' | 'percent' | 'number' | 'text' | 'date' | 'select'

/** One piece of evidence behind a confidence score, in words. */
export interface ConfidenceSignal {
  signal: 'deal_count' | 'field_exists' | 'veto' | 'kind_agreement' | 'plausibility'
  weight: number | null
  score: number
  detail: string
}

/**
 * How sure the engine is, and why.
 *
 * `auto` is accepted without a person. `review` is ranked for one. `weak` is
 * shown last and marked thin. The basis travels with the score because a number
 * a person cannot interrogate is a number they cannot act on.
 */
export interface DatapointConfidence {
  score: number
  band: 'auto' | 'review' | 'weak'
  /** Why the score is held in the weak band whatever else it scores — one live
   *  deal, one file, values that are all 0 — or null when it is not. */
  cap?: string | null
  basis: ConfidenceSignal[]
}

export interface DiscoveredDatapoint {
  id: string
  /** The label as the survey normalizes it — the identity of the thing. */
  label: string
  /** The label as a document actually wrote it. */
  displayLabel: string
  proposedName: string
  proposedLabel: string
  proposedKind: DiscoveredKind
  proposedGroup: string
  proposedUnit: string | null
  /** How many live deals state it, counted from the records that still exist.
   *  The number that matters. (An older server sends its stored tally here.) */
  dealCount: number
  /** The tally the harvest kept, shown for transparency beside the live count. */
  recordedDealCount?: number
  occurrenceCount: number
  /** The live deals behind `dealCount`, newest first, at most 50. */
  deals?: DiscoveredDeal[]
  /** The distinct files the live readings came from. */
  files?: DiscoveredFile[]
  /** Readings whose record still exists, one per deal, file and cell. */
  sightings: DiscoveredSighting[]
  /** How many live deals, files and readings stand behind the row in all —
   *  more than `deals`, `files` and `sightings` hold when the server capped
   *  them. Absent from an older server: the lists are then all there is. */
  dealTotal?: number
  fileTotal?: number
  readingTotal?: number
  /** Stored readings left out because their record no longer exists or is archived. */
  staleSightingCount?: number
  /** A plain sentence when the recorded tally and the live count differ. */
  evidenceNote?: string | null
  status: 'proposed' | 'accepted' | 'dismissed'
  decidedBy: string | null
  decidedAt: string | null
  decisionNote: string | null
  firstSeenAt: string | null
  lastSeenAt: string | null
  /** True when the catalogue already has this field — then accepting teaches
   *  the parser a word rather than creating anything. */
  fieldExists: boolean
  confidence: DatapointConfidence
}

/** What re-reading one stored original found at a reading's cell. */
export type DiscoveredVerifyStatus =
  | 'confirmed' | 'label_moved' | 'value_mismatch' | 'source_unavailable' | 'not_checked'

/**
 * One reading, checked. `ingestionId`, `document`, `sheet` and `cell` are the
 * reading's RECORDED place, exactly as the row's `sightings` carry them, so a
 * verdict is matched to the reading it judges.
 */
export interface DiscoveredVerifyResult {
  ingestionId: string | null
  document: string | null
  sheet: string | null
  cell: string | null
  status: DiscoveredVerifyStatus
  /** The label the file states where the value was read: at the recorded cell,
   *  or — when the label moved — at its new place. A value_mismatch may name a
   *  DIFFERENT label: the one now at the recorded cell. Null when none reads there. */
  foundLabel: string | null
  /** The value the harvest's own rule finds beside `foundLabel`. */
  foundValue: unknown
  /** The value the reading stored. */
  expectedValue: unknown
  /** Where the label reads now, for label_moved; null otherwise. */
  foundSheet?: string | null
  foundCell?: string | null
  /** Why the reading was not checked or could not be (a file too large for the time left,
   *  no stored copy, a record the account may not open); null for a reading that was read. */
  reason?: string | null
}

export interface DiscoveredVerification {
  checked: number
  results: DiscoveredVerifyResult[]
  summary: {
    confirmed: number
    labelMoved: number
    valueMismatch: number
    unavailable: number
    notChecked: number
  }
}

/**
 * Re-open the stored originals behind a found datapoint and read each cell
 * again, by the rule the harvest used. Reads only — it writes nothing, so an
 * account that may not decide the row may still ask.
 */
export function verifyDiscovered(id: string): Promise<DiscoveredVerification> {
  return api
    .post(`/discovered-datapoints/${encodeURIComponent(id)}/verify`)
    .then((r) => toCamelCase<DiscoveredVerification>(r.data))
}
